// frontend/src/pages/settings/users-roles/PasswordOnceDialog.tsx
import { useState } from 'react';
import { Check, Copy, KeyRound } from 'lucide-react';

interface Props {
  username: string;
  password: string;
  onClose: () => void;
}

/** La contraseña generada se muestra UNA sola vez: el servidor no la vuelve a entregar. */
export function PasswordOnceDialog({ username, password, onClose }: Props) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopyFailed(false);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Sin portapapeles (navegador viejo o sin permiso): la contraseña ya está a la vista.
      setCopyFailed(true);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] p-6 shadow-xl">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-5 h-5 text-[var(--color-primary)]" />
          <h3 className="text-base font-semibold text-[var(--color-text)]">Contraseña generada</h3>
        </div>

        <p className="text-sm text-[var(--color-text-secondary)] mb-3">
          Anotá estos datos ahora: la contraseña no se vuelve a mostrar.
        </p>

        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-hover)] p-3 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-[var(--color-text-muted)]">Usuario</span>
            <span className="text-sm font-medium text-[var(--color-text)]">@{username}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-[var(--color-text-muted)]">Contraseña</span>
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono px-2 py-1 rounded bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text)] select-all">
                {password}
              </code>
              <button
                onClick={copy}
                className="p-1.5 rounded hover:bg-[var(--color-surface)] text-[var(--color-text-muted)]"
                title="Copiar"
              >
                {copied ? <Check className="w-4 h-4 text-[var(--color-success)]" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {copyFailed && (
          <p className="mt-2 text-xs text-amber-600">No se pudo copiar, seleccionalo a mano.</p>
        )}

        <p className="mt-3 text-xs text-[var(--color-text-muted)]">
          El usuario deberá cambiarla en su primer ingreso.
        </p>

        <div className="mt-5 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90"
          >
            Listo
          </button>
        </div>
      </div>
    </div>
  );
}
